import { cn } from '@/lib/utils';
import { FormError } from '@/libs/core/src/lib/types/ErrorResponse.type';
import React, { useCallback, useId } from 'react';
import { Icon } from '../Icon';
import { Icons } from '../icons';
import { TrivialTooltip } from '../TrivialTooltip';
import { Label } from '../ui/label';
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Select as UiSelect,
} from '../ui/select';
import { FieldErrors } from './FieldErrors';
import Hint from './Hint';

export type SelectOption = {
  value: any;
  text: string | React.ReactNode;
  disabled?: boolean;
};

export type SelectProps = {
  name: string;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  label?: React.ReactNode;
  options?: SelectOption[];
  value?: any;
  onChange: (val: any) => void;
  placeholder?: string;
  disabled?: boolean;
  errors?: FormError;
  inlineSelect?: boolean;
  inlineLabelWidth?: string;
  inlineInputWidth?: string;
  hint?: string;
  hintOnClick?: () => void;
  alwaysReturnArray?: boolean;
  showLabel?: boolean;
  noDarkMode?: boolean;
  loading?: boolean; // TBD
  tooltip?: string;
  onClear?: () => void;
  clearable?: boolean;
};

export const Select: React.FC<SelectProps> = ({
  name,
  className,
  labelClassName,
  inputClassName,
  label,
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  disabled,
  errors,
  inlineSelect = false,
  inlineLabelWidth,
  inlineInputWidth,
  hint,
  hintOnClick,
  alwaysReturnArray,
  showLabel = true,
  noDarkMode = false,
  loading = false,
  tooltip,
  onClear,
  clearable = false,
}) => {
  const uid = useId();
  const EMPTY_VALUE_PLACEHOLDER = `__EMPTY_VALUE_PLACEHOLDER__${uid}`;

  // // Helper to transform value for internal Select component
  // const transformValueOut = (internalValue: any) =>
  //   internalValue === EMPTY_VALUE_PLACEHOLDER ? '' : internalValue;

  // const transformValueIn = (externalValue: any) =>
  //   externalValue === '' || externalValue === undefined || externalValue === null
  //     ? EMPTY_VALUE_PLACEHOLDER
  //     : externalValue;

  // Helper to find the original option value by the internal string representation
  const findOriginalValue = (internalValue: string) => {
    if (internalValue === EMPTY_VALUE_PLACEHOLDER) {
      return '';
    }

    const option = options.find(
      opt => (opt.value === '' ? EMPTY_VALUE_PLACEHOLDER : String(opt.value)) === internalValue
    );

    return option ? option.value : internalValue;
  };

  const hasValue = value !== undefined && value !== null && value !== '';

  // Calculate the value to pass to UiSelect
  // Radix UI Select needs an empty string to clear, not undefined
  const selectValue =
    value === null || value === undefined
      ? ''
      : value === ''
        ? options?.some(opt => opt.value === '')
          ? EMPTY_VALUE_PLACEHOLDER
          : ''
        : typeof value === 'boolean' || typeof value === 'number'
          ? String(value)
          : value;

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // For clearable selects, we should pass null or undefined to properly clear
      const clearValue = alwaysReturnArray ? [] : undefined;
      onChange(clearValue);

      if (onClear) {
        onClear();
      }
    },
    [onChange, alwaysReturnArray, onClear]
  );

  return (
    <div
      className={cn(
        inlineSelect ? 'flex flex-row gap-1 items-center' : 'flex flex-col gap-1',
        className
      )}
    >
      {label && showLabel && (
        <Label
          htmlFor={name}
          className={cn(
            'text-sm font-medium',
            inlineSelect && inlineLabelWidth ? inlineLabelWidth : '',
            labelClassName
          )}
        >
          {label}
          {!!tooltip && <TrivialTooltip text={tooltip} />}
        </Label>
      )}
      <div className={cn(inlineSelect && inlineInputWidth ? inlineInputWidth : 'w-full')}>
        <div className="relative">
          <UiSelect
            value={selectValue}
            onValueChange={val => {
              const originalValue = findOriginalValue(val);
              onChange(alwaysReturnArray ? [originalValue] : originalValue);
            }}
            disabled={disabled}
          >
            <SelectTrigger
              id={name}
              className={cn(
                'w-full bg-white [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:truncate',
                loading ? 'pr-8' : '',
                inputClassName,
                noDarkMode ? 'dark:bg-white' : 'dark:bg-dark-gray-700'
              )}
            >
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-dark-gray-700">
              {options.map((opt, idx) => {
                let ffs = opt.value;

                if (opt.value === '') {
                  ffs = EMPTY_VALUE_PLACEHOLDER;
                } else if (typeof ffs === 'boolean' || typeof ffs === 'number') {
                  ffs = String(ffs);
                } else if (typeof ffs !== 'string') {
                  ffs = String(ffs?.id ?? ffs?.value ?? `${uid}-${idx}`);
                  // console.error({
                  //   option: opt,
                  //   value: ffs,
                  //   message:
                  //     'Option value is not string | number | boolean. Faking it to a string.',
                  // });
                }

                return (
                  <SelectItem key={ffs} value={ffs} disabled={opt.disabled}>
                    {opt.text}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </UiSelect>
          {loading && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10">
              <Icon icon={Icons.loading} className="opacity-50 h-4 w-4 shrink-0 animate-spin" />
            </div>
          )}
          {clearable && hasValue && !disabled && !loading && (
            <div
              className="absolute right-8 top-1/2 transform -translate-y-1/2 opacity-50 hover:opacity-100 cursor-pointer z-10"
              onClick={handleClear}
            >
              <Icon icon={Icons.close} className="h-4 w-4 shrink-0" />
            </div>
          )}
        </div>
        {!!hint && <Hint onClick={hintOnClick}>{hint}</Hint>}
        <FieldErrors errors={errors} />
      </div>
    </div>
  );
};

export default Select;
